import React, { useEffect, useState } from 'react';
import { Landmark } from 'lucide-react';
import api from '../api/client';

/**
 * Unified board-approval UI shared by IT Tickets and IT Accessory Requests.
 * Both modules have an identical approval lifecycle, so the state box, reminder
 * button and submission form live here once.
 *
 * basePath — '/tickets' | '/accessory-requests'
 * info     — latest approval row from GET {basePath}/{id}/board-approval
 */

// Offered as the default; IT can type any authorised approver per request
const DEFAULT_APPROVER = 'duminda@clsynergy.com';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const STATE = {
  pending:    { label: '⏳ Awaiting board decision', color: '#a78bfa', bg: 'rgba(124,58,237,0.08)', border: 'rgba(124,58,237,0.25)' },
  approved:   { label: '✓ Approved by the board',    color: '#4ade80', bg: 'rgba(22,163,74,0.08)',  border: 'rgba(22,163,74,0.25)' },
  rejected:   { label: '✖ Rejected by the board',    color: '#f87171', bg: 'rgba(220,38,38,0.08)',  border: 'rgba(220,38,38,0.25)' },
  superseded: { label: 'Previous request superseded', color: '#94a3b8', bg: 'rgba(148,163,184,0.08)', border: 'rgba(148,163,184,0.25)' },
  cancelled:  { label: '⊘ Request withdrawn by IT',   color: '#94a3b8', bg: 'rgba(148,163,184,0.08)', border: 'rgba(148,163,184,0.25)' },
};

/** Small status chip for data grids — identical in both modules. */
export function BoardApprovalChip({ status }) {
  if (!status) return null;
  const map = {
    approved: ['✓ Board Approved', '#4ade80', 'rgba(22,163,74,0.15)', 'rgba(22,163,74,0.3)'],
    rejected: ['✖ Board Rejected', '#f87171', 'rgba(220,38,38,0.15)', 'rgba(220,38,38,0.3)'],
    pending:  ['⏳ Board Pending',  '#a78bfa', 'rgba(124,58,237,0.15)', 'rgba(124,58,237,0.3)'],
  };
  const m = map[status];
  if (!m) return null;
  return (
    <span className="text-xs px-1.5 py-0.5 rounded-full"
      style={{ background: m[2], color: m[1], border: `1px solid ${m[3]}`, fontSize: 9.5, whiteSpace: 'nowrap' }}>
      {m[0]}
    </span>
  );
}

/** Read-only summary of the current approval state (cost, quotation, remark, reminders, decision). */
export function BoardApprovalState({ info, onRemind, remindSending, remindSent, onCancel, cancelling }) {
  if (!info) return null;
  const s = STATE[info.status] || STATE.superseded;
  const reminders = Array.isArray(info.reminders) ? info.reminders : [];
  return (
    <div className="rounded-lg p-3 text-xs" style={{ background: s.bg, border: `1px solid ${s.border}` }}>
      <div className="font-semibold mb-1" style={{ color: s.color }}>{s.label}</div>
      <div style={{ color: '#94a3b8' }}>
        Approver: <strong style={{ color: '#cbd5e1' }}>{info.approver_email || DEFAULT_APPROVER}</strong>
        <br/>
        Estimated cost: LKR {Number(info.estimation_cost || 0).toLocaleString()}
        {info.quotation_filename ? ` · Quotation: ${info.quotation_filename}` : ''}
        {info.decision_at
          ? ` · Decided: ${new Date(info.decision_at).toLocaleString('en-GB')}`
          : ` · Sent: ${new Date(info.created_at).toLocaleString('en-GB')}`}
      </div>
      {info.rejection_reason && <div className="mt-1" style={{ color: '#f87171' }}>Reason: {info.rejection_reason}</div>}
      {info.status === 'cancelled' && (
        <div className="mt-1" style={{ color: '#94a3b8' }}>
          Withdrawn {info.cancelled_at ? new Date(info.cancelled_at).toLocaleString('en-GB') : ''}
          {info.cancelled_by_name ? ` by ${info.cancelled_by_name}` : ''}
          {info.cancellation_reason ? ` · Reason: ${info.cancellation_reason}` : ''}
          <div style={{ color: '#64748b' }}>The Approve / Reject links sent to the approver no longer work.</div>
        </div>
      )}
      {info.admin_remark && <div className="mt-1" style={{ color: '#94a3b8' }}>Remark sent: {info.admin_remark}</div>}
      {reminders.length > 0 && (
        <div className="mt-2 rounded-md px-2 py-1.5" style={{ background: 'rgba(217,119,6,0.08)', border: '1px solid rgba(217,119,6,0.2)' }}>
          <div className="font-semibold" style={{ color: '#fbbf24' }}>
            🔔 {reminders.length} reminder{reminders.length > 1 ? 's' : ''} sent to the approver
          </div>
          <div className="mt-1 space-y-0.5">
            {reminders.map((r, i) => (
              <div key={i} className="flex items-baseline gap-1.5" style={{ color: '#94a3b8' }}>
                <span style={{ color: '#fbbf24', fontVariantNumeric: 'tabular-nums' }}>#{i + 1}</span>
                <span style={{ color: '#cbd5e1' }}>
                  {r.at ? new Date(r.at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Time not recorded'}
                </span>
                {r.by_name && <span style={{ color: '#64748b' }}>· by {r.by_name}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      {info.status === 'pending' && (onRemind || onCancel) && (
        <>
          <div className="mt-1" style={{ color: '#64748b' }}>
            Withdraw the request to stop the approver's links working — then you can send it to someone else.
          </div>
          <div className="mt-2 flex gap-2 flex-wrap">
            {onRemind && (
              <button type="button" disabled={remindSending} onClick={onRemind}
                className="text-xs px-3 py-1.5 rounded-lg font-medium"
                style={{ background: 'rgba(217,119,6,0.15)', color: '#fbbf24', border: '1px solid rgba(217,119,6,0.35)', cursor: remindSending ? 'default' : 'pointer' }}>
                {remindSending ? 'Sending…' : remindSent ? '✓ Reminder Sent' : '🔔 Send Reminder to Approver'}
              </button>
            )}
            {onCancel && (
              <button type="button" disabled={cancelling} onClick={onCancel}
                className="text-xs px-3 py-1.5 rounded-lg font-medium"
                style={{ background: 'rgba(220,38,38,0.12)', color: '#f87171', border: '1px solid rgba(220,38,38,0.35)', cursor: cancelling ? 'default' : 'pointer' }}>
                {cancelling ? 'Withdrawing…' : '⊘ Withdraw Request'}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Full panel: state + submission form. Used inline (accessory requests) and
 * inside the board modal (tickets).
 */
export default function BoardApprovalPanel({ basePath, recordId, info, onChange, heading = true, itemLabel = 'request' }) {
  const [form, setForm] = useState({ estimation_cost: '', quotation: null, admin_remark: '', approver_email: info?.approver_email || DEFAULT_APPROVER });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [remindSending, setRemindSending] = useState(false);
  const [remindSent, setRemindSent] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Default to whoever the last request went to, so re-sending after a withdrawal is one click
  useEffect(() => {
    setForm(f => (f.approver_email ? f : { ...f, approver_email: info?.approver_email || DEFAULT_APPROVER }));
  }, [info?.approver_email]);

  // meta.restoredStatus lets the page put the record's own status badge back after a withdrawal
  const refresh = async (meta) => {
    try {
      const r = await api.get(`${basePath}/${recordId}/board-approval`);
      onChange?.(r.data, meta);
    } catch { /* keep current state */ }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (!form.estimation_cost || parseFloat(form.estimation_cost) <= 0) {
      setError('Please enter a valid estimation cost.'); return;
    }
    const approver = (form.approver_email || '').trim();
    if (!EMAIL_RE.test(approver)) {
      setError("Please enter the approver's email address."); return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('estimation_cost', form.estimation_cost);
      fd.append('approver_email', approver);
      if (form.admin_remark) fd.append('admin_remark', form.admin_remark);
      if (form.quotation) fd.append('quotation', form.quotation);
      await api.post(`${basePath}/${recordId}/board-approval`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm({ estimation_cost: '', quotation: null, admin_remark: '', approver_email: approver });
      await refresh();
    } catch (err) { setError(err.response?.data?.error || 'Failed to send for board approval'); }
    finally { setSaving(false); }
  };

  const remind = async () => {
    setRemindSending(true); setError('');
    try {
      await api.post(`${basePath}/${recordId}/board-approval/remind`);
      setRemindSent(true); setTimeout(() => setRemindSent(false), 4000);
      await refresh();
    } catch (err) { setError(err.response?.data?.error || 'Failed to send reminder'); }
    finally { setRemindSending(false); }
  };

  const cancelRequest = async () => {
    const reason = window.prompt('Withdraw this approval request?\n\nThe approver is told it is cancelled and their Approve / Reject links stop working.\n\nReason (optional):');
    if (reason === null) return; // dialog dismissed
    setCancelling(true); setError('');
    try {
      const r = await api.post(`${basePath}/${recordId}/board-approval/cancel`, { reason });
      await refresh({ restoredStatus: r.data?.status });
    } catch (err) { setError(err.response?.data?.error || 'Failed to withdraw the approval request'); }
    finally { setCancelling(false); }
  };

  // Once approved the request is locked — view only, no resubmission
  const locked = info?.status === 'approved';

  return (
    <div className="rounded-xl p-4 space-y-3" style={{ background: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.25)' }}>
      {heading && (
        <div className="flex items-center gap-2">
          <Landmark size={14} style={{ color: '#a78bfa' }}/>
          <span className="text-sm font-semibold" style={{ color: '#a78bfa' }}>Board Approval</span>
        </div>
      )}
      {error && <div className="text-xs" style={{ color: '#f87171' }}>{error}</div>}

      <BoardApprovalState info={info} onRemind={remind} remindSending={remindSending} remindSent={remindSent}
        onCancel={cancelRequest} cancelling={cancelling}/>

      {!locked && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#475569' }}>Estimation Cost (LKR) *</label>
              <input type="number" min="1" step="0.01" value={form.estimation_cost}
                onChange={e => setForm(f => ({ ...f, estimation_cost: e.target.value }))}
                className="input-dark" placeholder="e.g. 15000"/>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#475569' }}>Quotation <span style={{ color: '#334155' }}>(optional)</span></label>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                onChange={e => setForm(f => ({ ...f, quotation: e.target.files?.[0] || null }))}
                className="text-xs" style={{ color: '#94a3b8' }}/>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: '#475569' }}>Send Approval To *</label>
            <input type="email" value={form.approver_email}
              onChange={e => setForm(f => ({ ...f, approver_email: e.target.value }))}
              className="input-dark" placeholder="approver@clsynergy.com"/>
            <div className="text-xs mt-1" style={{ color: '#64748b' }}>
              Any authorised approver — use a stand-in when the usual approver is unavailable.
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: '#475569' }}>Remark to Approver <span style={{ color: '#334155' }}>(optional)</span></label>
            <textarea rows={2} value={form.admin_remark}
              onChange={e => setForm(f => ({ ...f, admin_remark: e.target.value }))}
              className="input-dark" placeholder="Any note for the approver — included in the approval email"/>
          </div>
          <button type="button" disabled={saving} onClick={submit}
            className="btn-primary w-full justify-center" style={{ background: 'linear-gradient(135deg,#7c3aed,#6d28d9)' }}>
            {saving ? 'Sending…' : info?.status === 'pending' ? 'Replace Request & Send Again' : 'Send for Board Approval'}
          </button>
          <div className="text-xs" style={{ color: '#64748b' }}>
            Sends an email to <strong>{(form.approver_email || DEFAULT_APPROVER).trim()}</strong> with one-time Approve / Reject links.
            The decision automatically updates this {itemLabel} and notifies IT. Every send, reminder,
            withdrawal and decision is recorded with its time in the timeline.
          </div>
        </div>
      )}
    </div>
  );
}
